import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { chromeCopy } from '@/i18n/chrome'
import { LanguageSwitcher } from '@/i18n/LanguageSwitcher'
import { href, toLogicalPath } from '@/i18n/locale'
import { useLocale } from '@/i18n/useLocale'
import { cn } from '@/lib/utils'

interface HeaderProps {
  hasPromoBar?: boolean
}

const SCROLL_RANGE = 260
const HEIGHT_OPEN = 72
const HEIGHT_PACKED = 56
const RADIUS_OPEN = 16
const RADIUS_PACKED = 28

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

export function Header({ hasPromoBar = false }: HeaderProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const locale = useLocale()
  const copy = chromeCopy[locale]
  const logicalPath = toLogicalPath(location.pathname)
  const homePath = href('/', locale)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [scrolled, setScrolled] = useState(false)
  // Home's hero is always the dark "ink" surface, and it's under the header
  // at scrollY 0 on first paint — start in that state instead of the default
  // (white) one, which otherwise flashes for a beat before the scroll effect
  // below corrects it on mount.
  const [overInk, setOverInk] = useState(() => logicalPath === '/')
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const sheetId = useId()
  const sheetRef = useRef<HTMLDivElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const barRef = useRef<HTMLDivElement>(null)
  const reduceMotionRef = useRef(false)

  const items = useMemo(
    () => [
      { label: copy.services, href: logicalPath === '/' ? '#services' : `${homePath}#services` },
      { label: copy.process, href: logicalPath === '/' ? '#how-we-work' : `${homePath}#how-we-work` },
      { label: copy.about, href: `${href('/about', locale)}#about` },
    ],
    [copy.about, copy.process, copy.services, homePath, locale, logicalPath],
  )

  const isHomePage = logicalPath === '/'
  const isWorkshopKM = logicalPath === '/workshop-km'
  const isDoctorsAgent = logicalPath.startsWith('/doctors-agent')
  const hasOwnSections = isHomePage || logicalPath === '/dentaire'
  const hashLinkItems = items.filter((item) => item.href.startsWith('#'))
  const hideHeader = isWorkshopKM || isDoctorsAgent

  useEffect(() => {
    if (hideHeader) return

    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const syncMotion = () => {
      reduceMotionRef.current = media.matches
    }
    syncMotion()
    media.addEventListener('change', syncMotion)

    let ticking = false

    const measurePacked = () => {
      const bar = barRef.current
      if (!bar) return 0
      const styles = getComputedStyle(bar)
      const pad = (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0)
      const gap = parseFloat(styles.columnGap || styles.gap) || 0
      const parts = Array.from(bar.children).filter((node) => {
        return getComputedStyle(node as HTMLElement).display !== 'none'
      }) as HTMLElement[]

      const content = parts.reduce((sum, el) => {
        if (el.tagName === 'NAV') {
          const links = Array.from(el.children).filter((node) => {
            return getComputedStyle(node as HTMLElement).display !== 'none'
          }) as HTMLElement[]
          const navGap = parseFloat(getComputedStyle(el).columnGap || getComputedStyle(el).gap) || 0
          return (
            sum +
            links.reduce((linkSum, link) => linkSum + link.getBoundingClientRect().width, 0) +
            navGap * Math.max(0, links.length - 1)
          )
        }
        return sum + el.getBoundingClientRect().width
      }, 0)

      return Math.ceil(content + gap * Math.max(0, parts.length - 1) + pad + 2)
    }

    const applyBar = () => {
      const bar = barRef.current
      const wrap = wrapRef.current
      if (!bar || !wrap) return

      const full = wrap.clientWidth
      const packed = Math.min(measurePacked(), full)
      const progress = reduceMotionRef.current
        ? window.scrollY > 24 ? 1 : 0
        : clamp(window.scrollY / SCROLL_RANGE, 0, 1)
      const width = Math.round(full + (packed - full) * progress)
      const height = HEIGHT_OPEN + (HEIGHT_PACKED - HEIGHT_OPEN) * progress
      const radius = RADIUS_OPEN + (RADIUS_PACKED - RADIUS_OPEN) * progress

      bar.style.width = `${width}px`
      bar.style.height = `${height}px`
      bar.style.borderRadius = `${radius}px`
    }

    const detectOverInk = () => {
      const hero = document.getElementById('hero-section')
      if (!hero) {
        setOverInk(false)
        return
      }
      const heroRect = hero.getBoundingClientRect()
      const overHero = heroRect.bottom > 24 && heroRect.top < 88
      const heroIsInk = hero.dataset.surface === 'ink'
      setOverInk(overHero && heroIsInk)
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const nextScrolled = currentScrollY > 24
      setScrolled((prev) => (prev === nextScrolled ? prev : nextScrolled))
      applyBar()
      detectOverInk()

      if (!hasOwnSections) {
        setActiveIndex(null)
        ticking = false
        return
      }

      const sections = hashLinkItems
        .map((item) => document.getElementById(item.href.substring(1)))
        .filter(Boolean) as HTMLElement[]

      if (sections.length === 0) {
        setActiveIndex(null)
        ticking = false
        return
      }

      const scrollPosition = currentScrollY + 200
      let foundActive = false

      for (let i = sections.length - 1; i >= 0; i--) {
        if (sections[i] && sections[i].offsetTop <= scrollPosition) {
          const originalIndex = items.findIndex((item) => item.href === hashLinkItems[i].href)
          if (originalIndex !== -1) {
            setActiveIndex(originalIndex)
            foundActive = true
          }
          break
        }
      }

      if (!foundActive && currentScrollY < 100) {
        setActiveIndex(null)
      }

      ticking = false
    }

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(handleScroll)
        ticking = true
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    handleScroll()
    return () => {
      media.removeEventListener('change', syncMotion)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [hideHeader, isHomePage, hasOwnSections, location.pathname, items])

  useEffect(() => {
    if (!isMobileMenuOpen) return
    // Lock scroll with position:fixed (not overflow:hidden) so the page can't
    // jump: overflow:hidden on body lets mobile browsers reset/recompute the
    // scroll position while locked, which breaks this sticky header's offset.
    const scrollY = window.scrollY
    const body = document.body
    const previousPosition = body.style.position
    const previousTop = body.style.top
    const previousWidth = body.style.width
    body.style.position = 'fixed'
    body.style.top = `-${scrollY}px`
    body.style.width = '100%'

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false)
        toggleRef.current?.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      body.style.position = previousPosition
      body.style.top = previousTop
      body.style.width = previousWidth
      window.scrollTo(0, scrollY)
      document.removeEventListener('keydown', onKey)
    }
  }, [isMobileMenuOpen])

  const scrollToSection = (href: string) => {
    if (href.startsWith('/')) {
      navigate(href)
      return
    }
    if (!hasOwnSections) {
      navigate(homePath)
      setTimeout(() => {
        document.getElementById(href.substring(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
      return
    }
    document.getElementById(href.substring(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const handleLogoClick = () => {
    if (isHomePage) window.scrollTo({ top: 0, behavior: 'smooth' })
    else navigate(homePath)
  }

  if (hideHeader) return null

  return (
    <header className={cn('z-[100] px-4 sm:px-6', isWorkshopKM ? 'relative' : 'sticky top-0')} style={{ paddingTop: hasPromoBar ? 48 : 16 }}>
      <div ref={wrapRef} className="relative mx-auto max-w-[1440px]">
        <div
          ref={barRef}
          style={{ maxWidth: '100%' }}
          className={cn(
            'mx-auto flex h-[72px] items-center gap-5 rounded-2xl px-5',
            'transition-[border-color,box-shadow] duration-[240ms] ease-[cubic-bezier(.2,.8,.2,1)] motion-reduce:transition-none',
            overInk
              ? 'border border-white/20 bg-[rgba(10,23,68,.6)] shadow-fw-nav backdrop-blur-[14px]'
              : scrolled
                ? 'border border-fw-hairline bg-[rgba(255,255,255,.94)] shadow-fw-nav-scrolled backdrop-blur-[10px]'
                : 'border border-fw-hairline bg-[rgba(255,255,255,.88)] shadow-fw-nav backdrop-blur-[10px]',
          )}
        >
          <button
            type="button"
            onClick={handleLogoClick}
            className={cn(
              'inline-flex shrink-0 items-center gap-2 text-[17px] font-bold tracking-[-0.01em]',
              overInk ? 'text-white' : 'text-fw-ink',
            )}
            aria-label={copy.homeAria}
          >
            Forteuno
            <span
              className={cn('size-[7px] rounded-full', overInk ? 'bg-fw-violet-electric' : 'bg-fw-violet')}
              aria-hidden
            />
          </button>

          <nav
            className="hidden min-w-0 flex-1 items-center justify-center gap-[22px] lg:flex"
            aria-label="Primary"
          >
            {items.map((item, index) => {
              const isRoute = item.href.startsWith('/')
              const isActive = isRoute
                ? location.pathname === item.href.split('#')[0]
                : hasOwnSections && activeIndex === index

              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => {
                    if (!isRoute && hasOwnSections) setActiveIndex(index)
                    scrollToSection(item.href)
                  }}
                  className={cn(
                    'text-sm transition-colors',
                    isActive
                      ? overInk
                        ? 'font-semibold text-white'
                        : 'font-semibold text-fw-ink'
                      : overInk
                        ? 'font-medium text-fw-ink-body hover:text-white'
                        : 'font-medium text-fw-graphite hover:text-fw-ink',
                  )}
                >
                  {item.label}
                </button>
              )
            })}
          </nav>

          <div className="ml-auto hidden shrink-0 items-center gap-3 lg:flex">
            <LanguageSwitcher overInk={overInk} />
            <Link
              to={href('/contact', locale)}
              className={cn('text-sm font-medium', overInk ? 'text-white' : 'text-fw-ink')}
            >
              {copy.contact}
            </Link>
            <button
              type="button"
              onClick={() => navigate('/discover#scheduler')}
              className={cn(
                'inline-flex min-h-11 shrink-0 items-center justify-center whitespace-nowrap rounded-full px-4 py-2.5 text-sm font-semibold',
                overInk
                  ? 'bg-fw-white text-fw-ink hover:bg-fw-lavender'
                  : 'bg-fw-violet text-white hover:bg-fw-violet-hover',
              )}
            >
              {copy.book}
            </button>
          </div>

          <button
            ref={toggleRef}
            type="button"
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            className={cn(
              'ml-auto grid size-11 place-items-center rounded-xl lg:hidden',
              overInk ? 'text-white' : 'text-fw-ink',
            )}
            aria-expanded={isMobileMenuOpen}
            aria-controls={sheetId}
            aria-label={isMobileMenuOpen ? copy.closeMenu : copy.openMenu}
          >
            {isMobileMenuOpen ? (
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div
            id={sheetId}
            ref={sheetRef}
            className="absolute inset-x-0 top-full z-50 mt-2 overflow-hidden rounded-2xl border border-fw-hairline bg-fw-white shadow-fw-nav-scrolled lg:hidden"
          >
            <div className="flex flex-col">
              {items.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => {
                    scrollToSection(item.href)
                    setIsMobileMenuOpen(false)
                  }}
                  className="min-h-[50px] px-5 text-left text-[15px] font-medium text-fw-ink"
                >
                  {item.label}
                </button>
              ))}
              <div className="flex flex-col gap-2 border-t border-fw-hairline p-4">
                <LanguageSwitcher className="justify-center" />
                <Link
                  to={href('/contact', locale)}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="inline-flex min-h-[50px] items-center justify-center rounded-xl border border-fw-hairline text-sm font-semibold text-fw-ink"
                >
                  {copy.contact}
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    navigate('/discover#scheduler')
                    setIsMobileMenuOpen(false)
                  }}
                  className="inline-flex min-h-[50px] items-center justify-center rounded-xl bg-fw-violet text-sm font-semibold text-white"
                >
                  {copy.book}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  )
}





